import axios, {parseResponseData} from '@/axiosInstance'
import {z} from 'zod'
import {
    Contact,
    NotificationPayload,
    PaginationParams,
    PaginationResponse,
    PatientAgeRange,
    ResponseType,
    TableFiltersPayload,
    TableOrder
} from '@/types'
import {
    EcmScore,
    UserByPatient,
    UserExtended,
    UserQualificationType,
    UsersColumn,
    UserSmall
} from '@/features/users/types'
import {captureException} from '@sentry/react'

// create user
export interface HttpCreateUserPayload extends NotificationPayload {
    // personal data
    lastName: string
    firstName: string
    email: string
    phoneNumber: string
    gender: string
    birthDate: string
    birthCityId?: string
    birthCountryId?: string
    taxCode: string
    maritalStatus?: string
    residenceAslDistrictId?: string
    residence?: {
        address: string
        streetNumber: string
        city: string
        postalCode: string
    }
    domicileAslDistrictId?: string
    domicile?: {
        address: string
        streetNumber: string
        city: string
        postalCode: string
    }
    // working data
    status: string
    startDate?: string
    candidatureDate?: string
    specializationId: string
    fm07?: number
    fm07AttachmentId?: string
    fm08?: number
    fm08AttachmentId?: string
    patientAgeRange?: PatientAgeRange
    workSector?: string
    workAreas?: string[]
    // contractual data
    contractType?: string
    vatNumber?: string
    weeklyHours?: number
    raExempt: boolean
    paymentMethod?: string
    iban?: string
    workSuitableUpTo?: string
    amount?: number
    // education data
    qualifications?: Array<{
        type: UserQualificationType
        institute: string
        sector: string
        graduationYear: number
        otherInformation?: string
    }>
    registrationNumber?: string
    registrationLocation?: string
    registrationDate?: string
    ecmScores?: EcmScore[]
    curriculumLastUpdate?: string
    curriculumAttachmentId?: string
    professionalInsuranceLastVerify?: string
    absenceOfIncompatibilityLastVerify?: number
    // access profile data
    roleId?: string
    permissionsOverwritten?: boolean
    permissions?: string[]
    // pec
    pec?: string
    // additional contacts
    contacts?: Array<{
        id?: string
        type: 'mobile_phone' | 'telephone' | 'email'
        contact: string
        label?: string | null
    }>
    // privacy
    privacyB: boolean
    privacyC: boolean
    fm68: boolean
}

type HttpCreateUserOptions = {
    params: {responseType: ResponseType}
    payload: HttpCreateUserPayload
}
export const httpCreateUser = ({params, payload}: HttpCreateUserOptions) => axios.post(`/users`, payload, {params})

// update user
export const HttpUpdateUserPayload = z
    .object({
        // personal data
        lastName: z.string(),
        firstName: z.string(),
        email: z.string(),
        phoneNumber: z.string(),
        gender: z.string(),
        birthDate: z.string(),
        birthCityId: z.string().nullable(),
        birthCountryId: z.string().nullable(),
        taxCode: z.string(),
        maritalStatus: z.string().nullable(),
        residenceAslId: z.string().nullable(),
        residence: z
            .object({
                address: z.string(),
                streetNumber: z.string().nullable(),
                city: z.string(),
                postalCode: z.string()
            })
            .nullable(),
        domicileAslId: z.string().nullable(),
        domicile: z
            .object({
                address: z.string(),
                streetNumber: z.string().nullable(),
                city: z.string(),
                postalCode: z.string()
            })
            .nullable(),
        // working data
        status: z.string(),
        startDate: z.string().nullable(),
        candidatureDate: z.string().nullable(),
        specializationId: z.string(),
        fm07Evaluation: z.number().nullable(),
        fm07EvaluationAttachmentId: z.string().nullable(),
        fm08Evaluation: z.number().nullable(),
        fm08EvaluationAttachmentId: z.string().nullable(),
        patientAgeRange: PatientAgeRange.optional(),
        workSector: z.string().nullable(),
        workAreas: z.array(z.string()).nullable(),
        // contractual data
        contractType: z.string().nullable(),
        vatNumber: z.string().nullable(),
        weeklyHours: z.number().nullable(),
        raExempt: z.boolean(),
        paymentMethod: z.string().nullable(),
        iban: z.string().nullable(),
        workSuitableUpTo: z.string().nullable(),
        amount: z.number().nullable(),
        // education data
        qualifications: z
            .array(
                z.object({
                    type: UserQualificationType,
                    institute: z.string(),
                    workSector: z.string(),
                    graduationYear: z.number(),
                    otherInformation: z.string().nullable()
                })
            )
            .nullable(),
        registrationNumber: z.string().nullable(),
        registrationLocation: z.string().nullable(),
        registrationDate: z.string().nullable(),
        ecmScores: z.array(EcmScore).nullable(),
        curriculumLastUpdate: z.string().nullable(),
        curriculumAttachmentId: z.string().nullable(),
        professionalInsuranceLastVerify: z.string().nullable(),
        absenceOfIncompatibilityLastVerify: z.number().nullable(),
        // access profile data
        roleId: z.string().nullable(),
        permissionsOverwritten: z.boolean().nullable(),
        permissions: z.array(z.string()).nullable(),
        // pec
        pec: z.string().email().nullable(),
        // additional contacts
        contacts: z.array(Contact).nullable(),
        // privacy
        privacyB: z.boolean(),
        privacyC: z.boolean(),
        fm68: z.boolean()
    })
    .partial()
export type HttpUpdateUserPayload = z.infer<typeof HttpUpdateUserPayload>

export type HttpUpdateUserOptions = {
    urlParams: {userId: string}
    params: {responseType: ResponseType}
    payload: HttpUpdateUserPayload
}

export const httpUpdateUser = ({urlParams, params, payload}: HttpUpdateUserOptions) =>
    axios.patch(`users/${urlParams.userId}`, payload, {params})

// Specializations
export type HttpGetUserSpecializationsOptions = {
    search?: string
} & PaginationParams

export const httpGetUserSpecializations = (params: HttpGetUserSpecializationsOptions) =>
    axios.get(`specializations`, {
        params
    })

// Get work areas list
export type HttpGetWorkAreasOptions = {
    search?: string
    responseType: ResponseType
} & PaginationParams

export const httpGetWorkAreas = (params: HttpGetWorkAreasOptions) => axios.get(`/work-areas`, {params})

// Get roles and access permissions
export const httpGetAccessProfiles = (params: {search?: string; responseType: ResponseType}) =>
    axios.get('/roles', {params})

export type HttpGetRoleOptions = {
    urlParams: {
        roleId: string
    }
    params: {
        responseType: ResponseType
    }
}

export const httpGetRole = ({urlParams, params}: HttpGetRoleOptions) => axios.get(`roles/${urlParams.roleId}`, {params})

export const httpGetPermissions = () => axios.get('/permissions')

/*Get users*/
export interface HttpGetUsersOptions {
    params: PaginationParams & {
        search?: string
        responseType?: ResponseType | 'index'
    }
    payload?: TableFiltersPayload & {
        sorts?: TableOrder[]
    }
}
export const httpGetUsers = ({params, payload}: HttpGetUsersOptions) => axios.post('users/index', payload, {params})

/*Delete user*/
export const httpDeleteUser = ({urlParams}: {urlParams: {userId: string}}) => axios.delete(`users/${urlParams.userId}`)

/*Export user*/
interface HttpExportUserOptions {
    payload: {
        selectedUserIds: string[]
        exceptedUserIds: string[]
        type: 'xlsx' | 'xls' | 'csv'
        filters?: TableFiltersPayload['filters']
        fields?: UsersColumn[]
        sorts?: TableOrder[]
    }
}
export const httpExportUser = ({payload}: HttpExportUserOptions) =>
    axios.post('users/export', payload, {responseType: 'blob'})

/*Get user*/
export type HttpGetUserOptions<TResponseType extends ResponseType> = {
    urlParams: {userId: string}
    params: {responseType: TResponseType}
}

const HttpGetUserResponse = z.object({
    small: UserSmall,
    extended: UserExtended
})

export const httpGetUser = async <TResponseType extends ResponseType>({
    urlParams,
    params
}: HttpGetUserOptions<TResponseType>) => {
    const response = await axios.get(`users/${urlParams.userId}`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetUserResponse.shape[params.responseType]
    })
}

export const httpSendInvite = ({urlParams}: {urlParams: {userId: string}}) => {
    return axios.post(`users/${urlParams.userId}/send-registration-email`)
}

/*Get users*/
export const HttpInfiniteUsersResponse = z.object({
    data: z.array(UserSmall),
    meta: PaginationResponse
})
export type HttpInfiniteUsersResponse = z.infer<typeof HttpInfiniteUsersResponse>

export interface HttpGetUsersBySpecializationOptions {
    params: {
        specialization: string
        workService: UserSmall['patientAgeRange']
        workSector: UserSmall['workSector']
    }
}
export const httpGetUsersBySpecialization = ({params}: HttpGetUsersBySpecializationOptions) =>
    axios.get('users/index-by-specialization', {params})

// Get users for aggregated print in medical record view
export interface HttpGetUsersByPatientOptions {
    params: PaginationParams & {
        patientId: string
        search?: string
    }
}

const HttpGetUsersByPatientResponse = z.object({
    data: z.array(UserByPatient),
    meta: PaginationResponse
})
export type HttpGetUsersByPatientResponse = z.infer<typeof HttpGetUsersByPatientResponse>

export const httpGetUsersByPatient = async ({params}: HttpGetUsersByPatientOptions) => {
    const response = await axios.get(`aggregated-prints/users`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetUsersByPatientResponse
    })
}

export interface HttpGetUsersByGroupOptions {
    params: {
        group: 'therapist' | 'referring_doctor'
        responseType: ResponseType
        search?: string
        limit: number
        page: number
    }
}
export const HttpGetUsersByGroupResponse = z.object({data: z.array(UserSmall), meta: PaginationResponse})
export type HttpGetUsersByGroupResponse = z.infer<typeof HttpGetUsersByGroupResponse>
export const httpGetUsersByGroup = async ({params}: HttpGetUsersByGroupOptions) => {
    const response = await axios.get(`users/index-by-group`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetUsersByGroupResponse
    })
}
